import GpClientApiBase, { GpSubmitAndWaitResponse, IGpExecuteResponse } from './GpApiClientBase';
import { FeatureSet, FeatureLayer, RasterData, MapImageLayer, Geometry, esriProjection } from 'cd-eft/utils/esriTypes';
import { appConfig } from 'cd-eft/config/AppConfig';
import stores from 'cd-eft/models/Stores';
import * as esriQuery from '@arcgis/core/rest/query';
import Query from '@arcgis/core/rest/support/Query';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import * as esriGeometryEngine from '@arcgis/core/geometry/geometryEngine';
import Polygon from '@arcgis/core/geometry/Polygon';
import Graphic from '@arcgis/core/Graphic';
import { explodeMultipartPolygon } from 'cd-eft/utils/gisUtils';
import { LoggerControllerApi } from 'cd-eft/services/eftPrjAdminApi';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

const __apiConfig = appConfig.hydrologyRefactorGpApiConfig;
const __gpSessionRoot = appConfig.gpSessionRoot;
const __demGeotiffFolder = appConfig.demGeotiffFolder;
const __appName: string = "NETS";

export default class DelineateWatershedGpApi extends GpClientApiBase {
  private readonly __gpSessionRoot: string;
  private readonly __demGeotiffFolder: string;
  private readonly __baseUrl: string;
  private readonly __createSessionFolderUrl: string;
  private readonly __deleteSessionFolderUrl: string;
  private readonly __loadSessionUrl: string;
  private readonly __exportSessionUrl: string;
  private readonly __convertGdbToGeotiffUrl: string;
  private readonly __invalidateSessionUrl: string;
  private readonly __uploadDEMUrl: string;
  private readonly __generateAOIDEMUrl: string;
  private readonly __generateContoursUrl: string;
  private readonly __generateHillshadeUrl: string;
  private readonly __generateDepthGridUrl: string;
  private readonly __generateStreamlinesUrl: string;
  private readonly __generateWatershedUrl: string;
  private readonly __updateWatershedUrl: string;
  private readonly __prepareLULCUrl: string;
  private readonly __prepareSoilsUrl: string;
  private readonly __calculateRcnUrl: string;
  private readonly __lulcMapServerUrl: string;
  private readonly __soilMapServerUrl: string;

  constructor() {
    super(__apiConfig.timeout);
    this.__gpSessionRoot = __gpSessionRoot;
    this.__demGeotiffFolder = __demGeotiffFolder;
    this.__baseUrl = __apiConfig.baseUrl;
    this.__createSessionFolderUrl = new URL(__apiConfig.createSessionFolderPath, this.__baseUrl).toString();
    this.__deleteSessionFolderUrl = new URL(__apiConfig.deleteSessionFolderPath, this.__baseUrl).toString();
    this.__loadSessionUrl = new URL(__apiConfig.loadSessionPath, this.__baseUrl).toString();
    this.__exportSessionUrl = new URL(__apiConfig.exportSessionPath, this.__baseUrl).toString();
    this.__convertGdbToGeotiffUrl = new URL(__apiConfig.convertGdbToGeotiffPath, this.__baseUrl).toString();
    this.__invalidateSessionUrl = new URL(__apiConfig.invalidateSessionPath, this.__baseUrl).toString();
    this.__uploadDEMUrl = new URL(__apiConfig.uploadDEMPath, this.__baseUrl).toString();
    this.__generateAOIDEMUrl = new URL(__apiConfig.generateAOIDEMPath, this.__baseUrl).toString();
    this.__generateContoursUrl = new URL(__apiConfig.generateContoursPath, this.__baseUrl).toString();
    this.__generateHillshadeUrl = new URL(__apiConfig.generateHillshadePath, this.__baseUrl).toString();
    this.__generateDepthGridUrl = new URL(__apiConfig.generateDepthGridPath, this.__baseUrl).toString();
    this.__generateStreamlinesUrl = new URL(__apiConfig.generateStreamlinesPath, this.__baseUrl).toString();
    this.__generateWatershedUrl = new URL(__apiConfig.generateWatershedPath, this.__baseUrl).toString();
    this.__updateWatershedUrl = new URL(__apiConfig.updateWatershedPath, this.__baseUrl).toString();
    this.__prepareLULCUrl = new URL(__apiConfig.prepareLULCPath, this.__baseUrl).toString();
    this.__prepareSoilsUrl = new URL(__apiConfig.prepareSoilsPath, this.__baseUrl).toString();
    this.__calculateRcnUrl = new URL(__apiConfig.calculateRcnPath, this.__baseUrl).toString();
    this.__lulcMapServerUrl = new URL(__apiConfig.lulcMapServer, this.__baseUrl).toString();
    this.__soilMapServerUrl = new URL(__apiConfig.soilMapServer, this.__baseUrl).toString();
  }

  public static async logGpError(
    errorCode: string,
    shortDescription?: string,
    gpSessionUuid?: string
  ): Promise<void> {
    try {
      const projectUuid = stores.AppStore.efh2Store.projectsStore.currentProjectId as string;
      const errorDto = {
        appName: __appName,
        errorCode: errorCode,
        gpSessionUuid: gpSessionUuid,
        projectUuid: projectUuid,
        shortDescription: shortDescription
      };
      await LoggerControllerApi.logAppUsingPOST(errorDto);
      console.warn(`ERROR in project ${projectUuid}, GP session ID  ${gpSessionUuid}: ${shortDescription}`);
    } catch {
      // pass
    }
  }

  public static async logStandardGpError(
    errorCode: string,
    gpService: string,
    gpSessionUuid?: string
  ): Promise<void> {
    await DelineateWatershedGpApi.logGpError(
      errorCode,
      `GP service ${gpService} has failed.`,
      gpSessionUuid);
  }

  public static async showStandardNotification(errorCode: string): Promise<void> {
    await MessageCenter.addNotificationFromCode(errorCode, [
      {
        callback: () => {
          MessageCenter.clearMessages();
        },
        text: "Cancel",
      },
    ]);
  }

  private static async getMapImageLayerFromGp(
    gpResponse: GpSubmitAndWaitResponse,
    resultName: string,
  ): Promise<MapImageLayer> {
    const mapImageLayer = await gpResponse.getResultMapImageLayer();
    mapImageLayer.id = resultName;
    const resultLayerName = resultName + '_layer';
    if (__resultLayersConfig && __resultLayersConfig[resultLayerName]) {
      mapImageLayer.title = __resultLayersConfig[resultLayerName].title;
      mapImageLayer.visible = __resultLayersConfig[resultLayerName].visible;
      mapImageLayer.listMode = __resultLayersConfig[resultLayerName].listMode;
      mapImageLayer.opacity = __resultLayersConfig[resultLayerName].opacity || 1.0;
    } else {
      mapImageLayer.title = resultName;
      mapImageLayer.visible = true;
      mapImageLayer.listMode = 'hide-children';
    }
    mapImageLayer.when(
      () => {
        mapImageLayer.sublayers.forEach((sublayer) => {
          sublayer.visible = (sublayer.title === resultName);
        });
      },
      (error) => {
        console.error(error);
        throw (error);
      }
    );
    return mapImageLayer;
  }

  private static async getFeatureResultFromGp(
    gpResponse: GpSubmitAndWaitResponse,
    resultName: string,
    predefinedFeatureSet: FeatureSet | undefined = undefined,
    createLayer = true
  ): Promise<IFeatureResult | undefined> {
    let result: IFeatureResult | undefined = undefined;
    const featureSet = predefinedFeatureSet ? predefinedFeatureSet : await gpResponse.getResultDataValue<FeatureSet>(resultName);
    let featureLayer: FeatureLayer | undefined = undefined;
    if (featureSet.features.length > 0) {
      const resultLayerName = resultName + '_layer';
      const objectIdField = featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name;
      if (createLayer) {
        if (__resultLayersConfig && __resultLayersConfig[resultLayerName]) {
          const props = { ...__resultLayersConfig[resultLayerName] };
          props.source = featureSet.features;
          props.objectIdField = objectIdField;
          featureLayer = new FeatureLayer(props);
          featureLayer.id = resultName;
        } else {
          featureLayer = new FeatureLayer({
            id: resultName,
            source: featureSet.features,
            title: resultName,
            visible: true,
            objectIdField: objectIdField,
            renderer: {
              type: 'simple',
              symbol: {
                type: 'simple-fill',
                style: 'none',
                color: [0, 0, 0, 0],
                outline: {
                  type: 'simple-line',
                  style: 'solid',
                  color: 'green',
                  width: '2px',
                } as unknown, //SimpleLineSymbol
              } as unknown, //SimpleFillSymbol
            } as any, //SimpleRenderer
          });
        }
      }
      result = {
        result_feature_set: featureSet,
        result_feature_layer: featureLayer
      };
    }
    return result;
  }

  private static async tryGetFeatureResult(
    gpResponse: GpSubmitAndWaitResponse,
    resultName: string,
    gpName: string,
    layerName: string,
    errorCode: string,
    in_session_id: string
  ) {
    let featureResult: IFeatureResult | undefined = undefined;
    let hasError: boolean = true;
    try {
      featureResult = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, resultName);
    } catch (e) {
      featureResult = undefined;
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed to retrieve result ${layerName} with error: ${(e as Error).message}`,
        in_session_id);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
      hasError = false;
    }
    return {
      hasError,
      featureResult
    };
  }

  async createGpSession(): Promise<string> {
    let gpSessionId: string = "";
    const gpName: string = "CreateSessionFolder";
    const errorCode = "Hydro_19";
    try {
      const url = this.__createSessionFolderUrl;
      const params: any = {
        in_server_folder: this.__gpSessionRoot
      };
      const response: IGpExecuteResponse = await this.__invokeGpExecute(url, params);
      const success = response.results[0].value as boolean;
      if (success) {
        gpSessionId = response.results[1].value as string;
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }
    } catch (e) {
      gpSessionId = "";
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed with error: ${(e as Error).message}`);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return gpSessionId;
  }

  async deleteGpSession(gpSessionId: string): Promise<boolean> {
    const url = this.__deleteSessionFolderUrl;
    const params: any = {
      in_server_folder: this.__gpSessionRoot,
      in_session_id: gpSessionId
    };
    const response = await this.__invokeGpExecute(url, params);
    const result: boolean = response.results[0].value;
    return result;
  }

  async invalidateGpSession(gpSessionId: string, currentStep: number, fullDelete?: boolean) {
    const url = this.__invalidateSessionUrl;
    const params: IInvalidateGpRequest = {
      in_server_folder: this.__gpSessionRoot,
      in_session_id: gpSessionId,
      in_step: currentStep,
      in_full_delete: fullDelete ? fullDelete : false,
      sys_message_prefix: '#MESSAGE#',
    }
    const response = await this.__invokeGpSubmitAndWaitForCompletion(url, params);
    return response;
  }

  async exportSessionEfh2(
    gpSessionId: string,
    projectName?: string,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<string> {
    let result: string = "";
    const gpName: string = "ExportSession";
    const errorCode = "Hydro_83";
    try {
      const url = this.__exportSessionUrl;
      const params: any = {
        in_server_folder: this.__gpSessionRoot,
        in_session_id: gpSessionId,
        in_project_name: projectName
      };
      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);
      const success = await gpResponse.getResultDataValue<boolean>("result_success");

      if (success) {
        const dataFile: any = await gpResponse.getResultDataValue('result_zipfile');
        result = dataFile.url;
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }

    } catch (e) {
      gpSessionId = "";
      result = ""
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed with error: ${(e as Error).message}`);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return result;
  }

  async convertGdbToGeotiff(
    gpSessionId: string,
    projectUuid: string,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<IConvertToGeotiffResponse | undefined> {
    let result: IConvertToGeotiffResponse | undefined = undefined;
    const gpName: string = "ConvertGdbToGeotiff";
    const errorCode = "Hydro_110";
    try {
      const url = this.__convertGdbToGeotiffUrl;
      const params: any = {
        in_server_folder: this.__gpSessionRoot,
        in_session_id: gpSessionId,
        // in_output_folder: this.__demGeotiffFolder,
        in_project_uuid: projectUuid
      };
      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);
      const success = await gpResponse.getResultDataValue<boolean>("result_success");

      if (success) {
        let geotiffFileUrl = "";
        const midify_Flag = await gpResponse.getResultDataValue<boolean>("result_modify_flag");
        if (midify_Flag) {
          const geotiffFile: any = await gpResponse.getResultDataValue("result_geotiff_file");
          geotiffFileUrl = geotiffFile.url;
        }
        result = {
          result_geotiff_file: geotiffFileUrl,
          result_modify_flag: midify_Flag
        };
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }

    } catch (e) {
      result = undefined
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed with error: ${(e as Error).message}`);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return result;
  }

  async uploadDem(
    in_session_id: string,
    in_dem: __esri.RasterData | File,
    in_dem_elevation_units: string,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<IDemUploadResponse | undefined> {
    let demUploadResult: IDemUploadResponse | undefined = undefined;
    const gpName: string = "DEMUpload";
    const errorCode = "Hydro_20";
    try {
      const url = this.__uploadDEMUrl;
      // upload the file
      let in_dem_file_info: any = undefined;
      if (in_dem) {
        if (Object.prototype.toString.call(in_dem) === '[object File]') {
          in_dem_file_info = await this.__uploadFile(url, in_dem as File);
        }
      }
      const params: any = {
        in_server_folder: this.__gpSessionRoot,
        in_session_id: in_session_id,
        in_dem: ((in_dem_file_info === undefined) ?
          this.__getRasterDataProperties(in_dem as RasterData) : in_dem_file_info),
        in_dem_elevation_units: in_dem_elevation_units
      };

      let gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);

      const success = await gpResponse.getResultDataValue<boolean>("result_success");
      if (success) {
        let result_dem_layer: MapImageLayer | undefined = undefined;
        let result_dem_boundary: IFeatureResult | undefined = undefined;

        try {
          result_dem_layer = await DelineateWatershedGpApi.getMapImageLayerFromGp(gpResponse, "result_dem");
        } catch (e) {
          result_dem_layer = undefined;
          await DelineateWatershedGpApi.logGpError(errorCode,
            `GP service ${gpName} has failed to retrieve result dem with error: ${(e as Error).message}`,
            in_session_id);
          await DelineateWatershedGpApi.showStandardNotification(errorCode);
        }

        if (result_dem_layer) {
          const url1 = url.replaceAll('DEMUpload', 'DEMUpload1');
          const params1: any = {
            in_server_folder: this.__gpSessionRoot,
            in_session_id: in_session_id
          };

          gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url1, params1, waitOptions);

          const success = await gpResponse.getResultDataValue<boolean>("result_success");
          if (success) {
            try {
              result_dem_boundary = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, "result_dem_boundary");
            } catch (e) {
              result_dem_boundary = undefined;
              await DelineateWatershedGpApi.logGpError(errorCode,
                `GP service ${gpName}1 has failed to retrieve result dem boundary with error: ${(e as Error).message}`,
                in_session_id);
              await DelineateWatershedGpApi.showStandardNotification(errorCode);
            }

            if (result_dem_boundary && result_dem_boundary.result_feature_set && result_dem_boundary.result_feature_layer) {
              demUploadResult = {
                result_dem_layer: result_dem_layer,
                result_dem_boundary: result_dem_boundary.result_feature_set,
                result_dem_boundary_layer: result_dem_boundary.result_feature_layer
              }
            }
          } else {
            await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
            await DelineateWatershedGpApi.showStandardNotification(errorCode);
          }
        }
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }
    } catch (e) {
      demUploadResult = undefined;
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed with error: ${(e as Error).message}`,
        in_session_id);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return demUploadResult;
  }

  async generateAoiDem(
    in_session_id: string,
    in_aoi?: FeatureSet,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions): Promise<IAoiDemResponse | undefined> {
    let aoiDemResult: IAoiDemResponse | undefined = undefined;
    const gpName: string = "GenerateAOIDEM";
    const errorCode = "Hydro_22";
    try {
      const url = this.__generateAOIDEMUrl;
      const params: any = {
        in_server_folder: this.__gpSessionRoot,
        in_session_id: in_session_id,
        in_aoi: in_aoi
      };

      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);

      const success = await gpResponse.getResultDataValue<boolean>("result_success");
      if (success) {
        let result_clipdem_layer: MapImageLayer | undefined = undefined;
        let result_aoi: IFeatureResult | undefined = undefined;

        try {
          result_clipdem_layer = await DelineateWatershedGpApi.getMapImageLayerFromGp(gpResponse, "result_clipdem");
        } catch (e) {
          result_clipdem_layer = undefined;
          await DelineateWatershedGpApi.logGpError(errorCode,
            `GP service ${gpName} has failed to retrieve result aoi dem with error: ${(e as Error).message}`,
            in_session_id);
          await DelineateWatershedGpApi.showStandardNotification(errorCode);
        }

        if (result_clipdem_layer) {
          const url1 = url.replaceAll('GenerateAOIDEM', 'GenerateAOIDEM1');
          const params1: any = {
            in_server_folder: this.__gpSessionRoot,
            in_session_id: in_session_id
          };

          const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url1, params1, waitOptions);

          const success = await gpResponse.getResultDataValue<boolean>("result_success");
          if (success) {
            try {
              result_aoi = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, "result_aoi");
            } catch (e) {
              result_aoi = undefined;
              await DelineateWatershedGpApi.logGpError(errorCode,
                `GP service ${gpName} has failed to retrieve result AOI with error: ${(e as Error).message}`,
                in_session_id);
              await DelineateWatershedGpApi.showStandardNotification(errorCode);
            }

            if (result_aoi && result_aoi.result_feature_set && result_aoi.result_feature_layer) {
              aoiDemResult = {
                result_clipdem_layer: result_clipdem_layer,
                result_aoi: result_aoi.result_feature_set,
                result_aoi_layer: result_aoi.result_feature_layer
              }
            }
          } else {
            await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
            await DelineateWatershedGpApi.showStandardNotification(errorCode);
          }
        }
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }
    } catch (e) {
      aoiDemResult = undefined;
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed with error: ${(e as Error).message}`,
        in_session_id);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return aoiDemResult;
  }

  async generateStreamlinesDummy(waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions): Promise<void> {
    try {
      const url = this.__generateStreamlinesUrl;
      const params: any = {
        in_server_folder: undefined,
        in_session_id: undefined,
        in_generate_streamlines: false,
        in_stream_threshold: undefined,
        in_culverts: undefined
      };
      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);
    } catch {
      // pass
    }
  }

  async generateStreamlines(
    in_session_id: string,
    in_generate_streamlines: boolean,
    in_stream_threshold: number,
    in_culverts?: FeatureSet,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<IStreamlineResponse | undefined> {
    let streamlineResult: IStreamlineResponse | undefined = undefined;
    const gpName: string = "GenerateStreamlines";
    const errorCode = "Hydro_61";
    try {
      const url = this.__generateStreamlinesUrl;
      const params: any = {
        in_server_folder: this.__gpSessionRoot,
        in_session_id: in_session_id,
        in_generate_streamlines: in_generate_streamlines,
        in_stream_threshold: in_stream_threshold,
        in_culverts: in_culverts
      };

      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);

      const success = await gpResponse.getResultDataValue<boolean>("result_success");
      if (success) {
        const excessCount = await gpResponse.getResultDataValue<boolean>("result_excess_count");
        streamlineResult = {
          result_excess_stream_count: excessCount
        }

        if (!excessCount) {
          const result_streamlines = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, "result_streamlines");
          if (result_streamlines) {
            streamlineResult.result_streamlines = result_streamlines.result_feature_set;
            streamlineResult.result_streamlines_layer = result_streamlines.result_feature_layer;
          }
        }

        if (in_culverts) {
          const result_culverts = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, "result_culverts");
          if (result_culverts) {
            streamlineResult.result_culverts = result_culverts.result_feature_set;
            streamlineResult.result_culverts_layer = result_culverts.result_feature_layer;
          }
        }
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }
    } catch (e) {
      streamlineResult = undefined;
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed with error: ${(e as Error).message}`,
        in_session_id);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return streamlineResult;
  }

  async generateWatershed(
    in_session_id: string,
    in_outlets: FeatureSet,
    in_dem_elevation_units: string,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<IWatershedResponse | undefined> {
    let watershedResult: IWatershedResponse | undefined = undefined;
    const gpName: string = "GenerateWatershed";
    const errorCode = "Hydro_88";
    try {
      const url = this.__generateWatershedUrl;
      const params: any = {
        in_server_folder: this.__gpSessionRoot,
        in_session_id: in_session_id,
        in_outlets: in_outlets,
        in_dem_elevation_units: in_dem_elevation_units
      };

      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);

      const success = await gpResponse.getResultDataValue<boolean>("result_success");
      if (success) {
        let result_watershed: IFeatureResult | undefined = undefined;
        let result_flowpath: IFeatureResult | undefined = undefined;
        let result_outlets: IFeatureResult | undefined = undefined;
        let hasError: boolean = true;

        const resultWatershed = await DelineateWatershedGpApi.tryGetFeatureResult(gpResponse, "result_watershed", gpName, "watershed", errorCode, in_session_id);
        hasError = resultWatershed.hasError;
        result_watershed = resultWatershed.featureResult;

        if (result_watershed && result_watershed.result_feature_set && result_watershed.result_feature_layer) {
          const resultFlowpath = await DelineateWatershedGpApi.tryGetFeatureResult(gpResponse, "result_flowpath", gpName, "flowpath", errorCode, in_session_id);
          hasError = resultFlowpath.hasError;
          result_flowpath = resultFlowpath.featureResult;

          if (result_flowpath && result_flowpath.result_feature_set && result_flowpath.result_feature_layer) {
            try {
              result_outlets = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, "result_outlets");
            } catch (e) {
              result_outlets = undefined;
              await DelineateWatershedGpApi.logGpError(errorCode,
                `GP service ${gpName} has failed to retrieve input outlets with error: ${(e as Error).message}`,
                in_session_id);
              await DelineateWatershedGpApi.showStandardNotification(errorCode);
              hasError = false;
            }

            if (result_outlets && result_outlets.result_feature_set && result_outlets.result_feature_layer) {
              watershedResult = {
                result_watershed: result_watershed.result_feature_set,
                result_watershed_layer: result_watershed.result_feature_layer,
                result_flowpath: result_flowpath.result_feature_set,
                result_flowpath_layer: result_flowpath.result_feature_layer,
                result_outlets: result_outlets.result_feature_set,
                result_outlets_layer: result_outlets.result_feature_layer
              }
              hasError = false;
            }
          }
        }

        if (hasError) {
          await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
          await DelineateWatershedGpApi.showStandardNotification(errorCode);
        }
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }
    } catch (e) {
      watershedResult = undefined;
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed with error: ${(e as Error).message}`,
        in_session_id);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return watershedResult;
  }

  async updateWatershed(
    in_session_id: string,
    in_watershed: FeatureSet,
    in_flowpath: FeatureSet,
    watershedModified: boolean,
    flowPathModified: boolean,
    in_dem_elevation_units: string,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<IWatershedResponse | undefined> {
    let watershedResult: IWatershedResponse | undefined = undefined;
    const gpName: string = "UpdateWatershed";
    const errorCode = "Hydro_81";
    try {
      const url = this.__updateWatershedUrl;
      const params: any = {
        in_server_folder: this.__gpSessionRoot,
        in_session_id: in_session_id,
        in_watershed_mod: watershedModified,
        in_flowpath_mod: flowPathModified,
        in_watershed: watershedModified ? in_watershed : undefined,
        in_flowpath: flowPathModified ? in_flowpath : undefined,
        in_dem_elevation_units: in_dem_elevation_units
      };

      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);

      const success = await gpResponse.getResultDataValue<boolean>("result_success");
      if (success) {
        let result_flowpath: IFeatureResult | undefined = undefined;
        let hasError: boolean = true;
        let result_watershed: IFeatureResult | undefined = undefined;

        const resultWatershed = await DelineateWatershedGpApi.tryGetFeatureResult(gpResponse, "result_watershed", gpName, "watershed", errorCode, in_session_id);
        hasError = resultWatershed.hasError;
        result_watershed = resultWatershed.featureResult;

        if (result_watershed && result_watershed.result_feature_set && result_watershed.result_feature_layer) {
          const resultFlowpath = await DelineateWatershedGpApi.tryGetFeatureResult(gpResponse, "result_flowpath", gpName, "flowpath", errorCode, in_session_id);
          hasError = resultFlowpath.hasError;
          result_flowpath = resultFlowpath.featureResult;

          if (result_flowpath && result_flowpath.result_feature_set && result_flowpath.result_feature_layer) {
            let result_flowpath_orig: IFeatureResult | undefined = undefined;
            let flowpath_orig: FeatureSet | undefined = undefined;
            let flowpath_orig_layer: FeatureLayer | undefined = undefined;
            try {
              result_flowpath_orig = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, "result_flowpath_orig");
              if (result_flowpath_orig) {
                flowpath_orig = result_flowpath_orig.result_feature_set;
                flowpath_orig_layer = result_flowpath_orig.result_feature_layer;
                if (flowpath_orig_layer) {
                  flowpath_orig_layer.visible = false;
                }
              }
            } catch {
              flowpath_orig = undefined;
              flowpath_orig_layer = undefined;
            }
            watershedResult = {
              result_watershed: result_watershed.result_feature_set,
              result_watershed_layer: result_watershed.result_feature_layer,
              result_flowpath: result_flowpath.result_feature_set,
              result_flowpath_layer: result_flowpath.result_feature_layer,
              result_flowpath_orig: flowpath_orig,
              result_flowpath_orig_layer: flowpath_orig_layer
            }
            hasError = false;
          }

          if (hasError) {
            await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
            await DelineateWatershedGpApi.showStandardNotification(errorCode);
          }
        } else {
          await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
          await DelineateWatershedGpApi.showStandardNotification(errorCode);
        }
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }
    } catch (e) {
      watershedResult = undefined;
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed with error: ${(e as Error).message}`,
        in_session_id);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return watershedResult;
  }

  async calculateRcn(
    in_session_id: string,
    in_soil: FeatureSet,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<ICalculateRcnResponse | undefined> {
    let rcnResult: ICalculateRcnResponse | undefined = undefined;
    const gpName: string = "CalculateRCN";
    const errorCode = "Hydro_87";
    try {
      const url = this.__calculateRcnUrl;
      const params: any = {
        in_server_folder: this.__gpSessionRoot,
        in_session_id: in_session_id,
        in_soil: in_soil
      };

      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);

      const success = await gpResponse.getResultDataValue<boolean>("result_success");
      if (success) {
        let result_rcn_watershed: IFeatureResult | undefined = undefined;
        let result_rcn_subareas: IFeatureResult | undefined = undefined;
        let hasError: boolean = true;

        try {
          result_rcn_watershed = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, "result_rcn_watershed");
        } catch (e) {
          result_rcn_watershed = undefined;
          await DelineateWatershedGpApi.logGpError(errorCode,
            `GP service ${gpName} has failed to retrieve result RCN watershed with error: ${(e as Error).message}`,
            in_session_id);
          await DelineateWatershedGpApi.showStandardNotification(errorCode);
          hasError = false;
        }

        if (result_rcn_watershed && result_rcn_watershed.result_feature_set && result_rcn_watershed.result_feature_layer) {
          try {
            result_rcn_subareas = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, "result_rcn_subareas");
          } catch (e) {
            result_rcn_subareas = undefined;
            await DelineateWatershedGpApi.logGpError(errorCode,
              `GP service ${gpName} has failed to retrieve result RCN subareas with error: ${(e as Error).message}`,
              in_session_id);
            await DelineateWatershedGpApi.showStandardNotification(errorCode);
            hasError = false;
          }
        }

        if (result_rcn_subareas && result_rcn_subareas.result_feature_set && result_rcn_subareas.result_feature_layer) {
          rcnResult = {
            result_rcn_watershed: result_rcn_watershed!.result_feature_set,
            result_rcn_watershed_layer: result_rcn_watershed!.result_feature_layer,
            result_rcn_subareas: result_rcn_subareas.result_feature_set,
            result_rcn_subareas_layer: result_rcn_subareas.result_feature_layer
          }
          hasError = false;
        }

        if (hasError) {
          await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
          await DelineateWatershedGpApi.showStandardNotification(errorCode);
        }
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }
    } catch (e) {
      rcnResult = undefined;
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed with error: ${(e as Error).message}`,
        in_session_id);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return rcnResult;
  }

  async loadSession(
    in_session_id: string,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<ILoadSessionResponse | undefined> {
    let loadResult: ILoadSessionResponse | undefined = {};
    const gpName: string = "LoadSession";
    const errorCode = "Hydro_82";

    try {
      const url = this.__loadSessionUrl;
      const params: any = {
        in_server_folder: this.__gpSessionRoot,
        in_session_id: in_session_id
      };

      let gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);

      let success = await gpResponse.getResultDataValue<boolean>("result_success");
      if (success) {
        let resultLayers: string[] = [];
        const resultLayerString = await gpResponse.getResultDataValue<string>("result_layers");
        if (resultLayerString) {
          resultLayers = resultLayerString.split(',')
        }

        if (resultLayers.length > 0) {
          let layerResult: MapImageLayer | undefined = undefined;
          const layers = [
            "result_dem",
            "result_clipdem",
            "result_hillshade",
            "result_depthgrid",
            "result_contours"
          ];
          for (const layer of layers) {
            if (resultLayers.includes(layer)) {
              try {
                layerResult = undefined;
                layerResult = await DelineateWatershedGpApi.getMapImageLayerFromGp(gpResponse, layer);
                if (layerResult) {
                  const layerProperty = `${layer}_layer`;
                  loadResult[layerProperty] = layerResult;
                }
              } catch {
                // PASS -- do nothing, errors should be expected, as not all results may be present
              }
            }
          }
        }

        const url1 = url.replaceAll('LoadSession', 'LoadSession1');

        gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url1, params, waitOptions);

        success = await gpResponse.getResultDataValue<boolean>("result_success");
        if (success) {
          let featureResult: IFeatureResult | undefined;
          const features = [
            "result_dem_boundary",
            "result_aoi",
            "result_streamlines",
            "result_culverts",
            "result_outlets",
            "result_watershed",
            "result_flowpath",
            "result_flowpath_orig",
            "result_soils",
            "result_landuse",
            "result_rcn_watershed",
            "result_rcn_subareas",
            "result_rcn_in_soils",
            // "result_rcn_in_landuse"
          ];
          for (const feature of features) {
            try {
              featureResult = undefined;
              featureResult = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, feature);
              if (featureResult && featureResult.result_feature_set && featureResult.result_feature_layer) {
                if (feature === "result_rcn_in_soils") {
                  featureResult.result_feature_layer.id = "result_soils";
                  loadResult["result_soils"] = featureResult.result_feature_set;
                  loadResult["result_soils_layer"] = featureResult.result_feature_layer;
                } else {
                  const featureLayer = `${feature}_layer`;
                  loadResult[feature] = featureResult.result_feature_set;
                  loadResult[featureLayer] = featureResult.result_feature_layer;
                }
              }
            } catch {
              // PASS -- do nothing, errors should be expected, as not all results may be present
            }
          }
        } else {
          loadResult = {};
          await DelineateWatershedGpApi.logStandardGpError(errorCode, `${gpName}1`, in_session_id);
          await DelineateWatershedGpApi.showStandardNotification(errorCode);
        }
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }
    } catch (e) {
      loadResult = {};
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} or ${gpName}1 has failed with error: ${(e as Error).message}`,
        in_session_id);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return loadResult;
  }

  async generateSingleFeatureResult(
    gpName: string,
    inputName: string,
    resultName: string,
    errorCode: string,
    getLayer: boolean,
    in_session_id: string,
    in_features: FeatureSet,
    addlInputName?: string,
    addl_in_features?: FeatureSet,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<IFeatureResult | undefined> {
    let featureResult: IFeatureResult | undefined = undefined;

    try {
      const url = this.__prepareLULCUrl.replaceAll("PrepareLULC", gpName);
      const params: any = {
        in_server_folder: this.__gpSessionRoot,
        in_session_id: in_session_id
      };
      params[inputName] = in_features;
      if (addlInputName && addl_in_features) {
        params[addlInputName] = addl_in_features
      }

      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);

      const success = await gpResponse.getResultDataValue<boolean>("result_success");
      if (success) {
        const result = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, resultName, undefined, getLayer);
        if (result && result.result_feature_set && (result.result_feature_layer || !getLayer)) {
          featureResult = result;
        } else {
          await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
          await DelineateWatershedGpApi.showStandardNotification(errorCode);
        }
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }
    } catch (e) {
      featureResult = undefined;
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed with error: ${(e as Error).message}`,
        in_session_id);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return featureResult;
  }

  async generateSingleResultMap(
    gpName: string,
    resultName: string,
    errorCode: string,
    in_session_id: string,
    in_dem_elevation_units?: string,
    in_contour_interval?: number,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<MapImageLayer | undefined> {
    let rasterResult: MapImageLayer | undefined = undefined;

    try {
      const url = this.__generateHillshadeUrl.replaceAll("GenerateHillshade", gpName);
      const params: any = {
        in_server_folder: this.__gpSessionRoot,
        in_session_id: in_session_id,
        in_dem_elevation_units: in_dem_elevation_units,
        in_contour_interval: in_contour_interval
      };

      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);

      const success = await gpResponse.getResultDataValue<boolean>("result_success");
      if (success) {
        const result = await DelineateWatershedGpApi.getMapImageLayerFromGp(gpResponse, resultName);
        if (result) {
          rasterResult = result;
        } else {
          await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
          await DelineateWatershedGpApi.showStandardNotification(errorCode);
        }
      } else {
        await DelineateWatershedGpApi.logStandardGpError(errorCode, gpName, in_session_id);
        await DelineateWatershedGpApi.showStandardNotification(errorCode);
      }
    } catch (e) {
      rasterResult = undefined;
      await DelineateWatershedGpApi.logGpError(errorCode,
        `GP service ${gpName} has failed with error: ${(e as Error).message}`,
        in_session_id);
      await DelineateWatershedGpApi.showStandardNotification(errorCode);
    }
    return rasterResult;
  }

  async delineateWatershed(
    requestParams: IDelineateWatershedRequestParams,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<Readonly<IDelineateWatershedResponse>> {
    const serviceUrl = ""; // this.__delineateWatershedUrl;

    // console.log('DELINEATE WATERSHED URL:', serviceUrl);
    // console.log('PROJECTION PARAMETERS:', { in_projection: requestParams.in_projection, v_unit: requestParams.in_dem_elevation_units });

    // upload the file
    let in_dem_file_info: any;
    if (requestParams.in_dem) {
      if (Object.prototype.toString.call(requestParams.in_dem) === '[object File]') {
        in_dem_file_info = await this.__uploadFile(serviceUrl, requestParams.in_dem as File);
        // console.log('File uploaded successfully');
      }
    }
    try {
      const params: IDelineateWatershedRequestParams = { ...requestParams };
      if (params.in_outlets) {
        params.in_projection = -1;
      }
      if (in_dem_file_info) {
        params.in_dem = in_dem_file_info;
        params.in_view_dem = true;
        //NOTE: in_view_dem = true, returning the result_dem RasterData is required if new file is uploaded,
        //otherwise FE will not have server reference for future calls and file will need to be uploaded repeatedy, very slow
      } else {
        params.in_dem = this.__getRasterDataProperties(requestParams.in_dem as RasterData);
        // params.in_view_dem = true;
      }

      // //override memory_db option
      // (params as any).sys_use_memory_db = false;

      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(serviceUrl, params, waitOptions);

      const result: IDelineateWatershedResponse = {
        out_result_manifest: [],
      };

      const rasterResultNames = ['result_dem', 'result_clipdem', 'result_hillshade', 'result_depthgrid'];
      for (const resultName of rasterResultNames) {
        try {
          result[resultName] = await gpResponse.getResultDataValue<RasterData>(resultName);
          result.out_result_manifest.push(resultName);
        } catch (error) {
          //nothing to see here, errors are expected if value does not exist on server
        }
      }

      const featureResultNames = [
        'result_dem_boundary',
        'result_aoi',
        'result_culverts',
        'result_outlets',
        'result_contours',
        'result_streamlines',
        'result_watershed',
        'result_upd_watershed',
        'result_flowpath',
      ];
      for (const resultName of featureResultNames) {
        try {
          const featureSet = await gpResponse.getResultDataValue<FeatureSet>(resultName);
          if (featureSet.features.length > 0) {
            result[resultName] = featureSet;
            result.out_result_manifest.push(resultName);
          }
        } catch (error) {
          //nothing to see here, errors are expected if value does not exist on server
        }
      }

      const mapImageLayerResultNames = [
        'result_dem',
        'result_clipdem',
        'result_hillshade',
        'result_depthgrid',
        'result_contours',
      ];
      for (const resultName of mapImageLayerResultNames) {
        if (result.out_result_manifest.includes(resultName)) {
          const mapImageLayer = await DelineateWatershedGpApi.getMapImageLayerFromGp(gpResponse, resultName);
          const resultLayerName = resultName + '_layer';
          result[resultLayerName] = mapImageLayer;
        }
      }

      const featureLayerResultNames = [
        'result_dem_boundary',
        'result_aoi',
        'result_culverts',
        'result_outlets',
        'result_streamlines',
        'result_watershed',
        'result_flowpath',
      ];
      for (const resultName of featureLayerResultNames) {
        if (result.out_result_manifest.includes(resultName)) {
          const resultLayerName = resultName + '_layer';
          const featureSet: FeatureSet = result[resultName];
          const resultFeature = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, resultName, featureSet);

          if (resultFeature) {
            result[resultLayerName] = resultFeature.result_feature_layer;
          }
        }
      }

      return result;
    }
    catch (err) {
      // console.log((err as any).messages.map(m => m.description));
      throw err;
    }
    finally {
      if (in_dem_file_info) {
        //perform non-critical cleanup operation, out of process
        this.__deleteFile(serviceUrl, in_dem_file_info)
          .then((success) => {
            console.info('Uploaded file cleaned up successfully.');
          })
          .catch((reason) => {
            //print non-critical error
            console.warn('Unable to delete file.');
            console.warn(reason);
          });
      }
    }
  }

  async prepareSal(
    input_WS_WATERSHED: FeatureSet,
    input_soil: FeatureSet,
    input_landuse: FeatureSet,
    input_sessionId: string,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<Readonly<IPrepareSalResponse>> {
    const url = ""; // this.__prepareSalUrl;

    const params: any = { input_WS_WATERSHED, input_soil, input_landuse, input_sessionId };
    const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);

    const response: IPrepareSalResponse = { out_result_manifest: [] };

    const featureResultNames = ['output_SAL_WATERSHED', 'result_soils', 'output_SAL_LANDUSE'];
    for (const resultName of featureResultNames) {
      try {
        const featureSet = await gpResponse.getResultDataValue<FeatureSet>(resultName);
        if (featureSet.features.length > 0) {
          response[resultName] = featureSet;
          response.out_result_manifest.push(resultName);
        }
      } catch (error) {
        //nothing to see here, errors are expected if value does not exist on server
      }
    }

    // const featureLayerResultNames = ['output_SAL_WATERSHED', 'result_soils', 'output_SAL_LANDUSE'];
    const featureLayerResultNames = ['output_SAL_WATERSHED', 'result_soils'];
    for (const resultName of featureLayerResultNames) {
      if (response.out_result_manifest.includes(resultName)) {
        const resultLayerName = resultName + '_layer';
        const featureSet: FeatureSet = response[resultName];
        const resultFeature = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, resultName, featureSet);
        if (resultFeature) {
          response[resultLayerName] = resultFeature.result_feature_layer;
        }
      }
    }

    return response;
  }

  async loadSessionEfh2(input_sessionId: string, waitOptions?: any): Promise<Readonly<ILoadSessionEfh2Response>> {
    const url = this.__loadSessionUrl;
    const params: any = { input_sessionId };
    const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);

    const response: ILoadSessionEfh2Response = { out_result_manifest: [] };

    const rasterResultNames = ['result_dem', 'result_clipdem', 'result_hillshade', 'result_depthgrid'];
    //const rasterResultNames = ['output_USER_DEM', 'output_AOI_DEM', 'output_AOI_HILLSHADE', 'output_AOI_DEPTHGRID'];
    for (const resultName of rasterResultNames) {
      try {
        response[resultName] = await gpResponse.getResultDataValue<RasterData>(resultName);
        response.out_result_manifest.push(resultName);
      } catch (error) {
        //nothing to see here, errors are expected if value does not exist on server
      }
    }

    const featureResultNames = [
      'result_dem_boundary',
      'result_aoi',
      'result_culverts',
      'result_outlets',
      'result_contours',
      'result_streamlines',
      'result_watershed',
      'result_flowpath',
      'output_SAL_WATERSHED',
      'result_soils',
      'output_SAL_LANDUSE',
      'output_RCN_WATERSHED',
      'output_RCN_SUBAREAS',
    ];
    for (const resultNameReal of featureResultNames) {
      let resultName = resultNameReal;
      if (resultNameReal === 'result_soils') {
        resultName = 'output_RCN_IN_SOILS';
      }
      try {
        const featureSet = await gpResponse.getResultDataValue<FeatureSet>(resultName);
        if (featureSet.features.length > 0) {
          response[resultNameReal] = featureSet;
          response.out_result_manifest.push(resultNameReal);
        } else if (resultNameReal === 'result_soils') {
          // if reloading soils, fall back to the result_soils feature set
          resultName = resultNameReal;
          const featureSet = await gpResponse.getResultDataValue<FeatureSet>(resultName);
          if (featureSet.features.length > 0) {
            response[resultNameReal] = featureSet;
            response.out_result_manifest.push(resultNameReal);
          }
        }
      } catch (error) {
        //nothing to see here, errors are expected if value does not exist on server
      }
    }

    const mapImageLayerResultNames = [
      'result_dem',
      'result_clipdem',
      'result_hillshade',
      'result_depthgrid',
      'result_contours',
    ];
    for (const resultName of mapImageLayerResultNames) {
      if (response.out_result_manifest.includes(resultName)) {
        const mapImageLayer = await DelineateWatershedGpApi.getMapImageLayerFromGp(gpResponse, resultName);
        const resultLayerName = resultName + '_layer';
        response[resultLayerName] = mapImageLayer;
      }
    }

    const featureLayerResultNames = [
      'result_dem_boundary',
      'result_aoi',
      'result_culverts',
      'result_outlets',
      'result_streamlines',
      'result_watershed',
      'result_flowpath',
      'output_SAL_WATERSHED',
      'result_soils',
      'output_RCN_WATERSHED',
      'output_RCN_SUBAREAS',
    ];
    for (const resultName of featureLayerResultNames) {
      if (response.out_result_manifest.includes(resultName)) {
        const resultLayerName = resultName + '_layer';
        const featureSet: FeatureSet = response[resultName];
        const resultFeature = await DelineateWatershedGpApi.getFeatureResultFromGp(gpResponse, resultName, featureSet);
        if (resultFeature) {
          response[resultLayerName] = resultFeature.result_feature_layer;
        }
      }
    }

    return response;
  }

  async fetchLULCFeatureSet(watershedFeatureSet: FeatureSet): Promise<__esri.FeatureSet> {
    const watershed = watershedFeatureSet.features[0].geometry;

    const query = new Query();
    query.spatialRelationship = 'intersects';
    query.returnGeometry = true;
    query.where = "1=1";
    query.outFields = ["*"];
    query.geometry = watershed;
    query.outSpatialReference = new SpatialReference({ wkid: 3857 });

    // Check the LULC cover the watershed in its entirety
    const fs_lulc = await esriQuery.executeQueryJSON(this.__lulcMapServerUrl, query);
    const fs_lulc_geometries = fs_lulc.features.map((feature) => {
      return feature.geometry;
    });
    const fs_lulc_union = esriGeometryEngine.union(fs_lulc_geometries);

    let innerGeometry = watershed;
    if (watershed.spatialReference.wkid !== fs_lulc_union.spatialReference.wkid) {
      if (!esriProjection.isLoaded()) {
        await esriProjection.load();
      }
      innerGeometry = esriProjection.project(watershed, fs_lulc_union.spatialReference) as Geometry;
    }
    if (!esriGeometryEngine.contains(fs_lulc_union, innerGeometry)) {
      await MessageCenter.addWarningFromCode('Hydro_78', [
        {
          callback: () => {
            MessageCenter.clearMessages();
          },
          text: 'OK',
        },
      ]);
    }

    return fs_lulc;
  }

  async fetchSoilFeatureSet(watershedFeatureSet: FeatureSet): Promise<__esri.FeatureSet | undefined> {
    let result: __esri.FeatureSet | undefined = undefined;

    try {
      const watershed = watershedFeatureSet.features[0].geometry;

      const query = new Query();
      query.spatialRelationship = 'intersects';
      query.returnGeometry = true;
      query.where = "MUKEY > 0";
      query.outFields = ["*"];
      query.geometry = watershed;
      query.outSpatialReference = new SpatialReference({ wkid: 3857 });

      const fs_hydro_soil = await esriQuery.executeQueryJSON(this.__soilMapServerUrl, query);

      const fromAttr = 'mukey';
      const toAttr = 'MUKEY';
      const changeField = fs_hydro_soil.fields.find((field) => (field.name === fromAttr));
      if (changeField !== undefined) {
        changeField.name = toAttr;
        fs_hydro_soil.features.forEach((f) => {
          f.attributes[toAttr] = f.attributes[fromAttr];
        });
      }

      const fs_soil_geometries = fs_hydro_soil.features.map((feature) => {
        return feature.geometry;
      });
      const unionResult = esriGeometryEngine.union(fs_soil_geometries);

      let innerGeometry = watershed;
      if (watershed.spatialReference.wkid !== unionResult.spatialReference.wkid) {
        if (!esriProjection.isLoaded()) {
          await esriProjection.load();
        }
        innerGeometry = esriProjection.project(watershed, unionResult.spatialReference) as Geometry;
      }

      const difference = esriGeometryEngine.difference(innerGeometry, unionResult);

      if (difference === null) {
        result = fs_hydro_soil;
      } else {
        // find the Object ID fiels and maximum OID in the dataset
        const oidField = fs_hydro_soil.fields.find((field) => (field.type === 'oid'))?.name!;
        let maxOid = 0;
        if (fs_hydro_soil.features.length > 0) {
          maxOid = Math.max(...fs_hydro_soil.features.map((f) => {
            return f.attributes[oidField] as number;
          }));
        }
        // add missing polygons to the soil feature set
        const notCovered: Geometry[] = (Array.isArray(difference) ? difference : [difference]);
        for (const geom of notCovered) {
          if (geom instanceof Polygon) {
            const newPolys = explodeMultipartPolygon(geom as Polygon);
            for (const poly of newPolys) {
              maxOid++;
              const newFeature = new Graphic({
                geometry: poly,
                attributes: { MUKEY: 0 },
              });
              newFeature.attributes[oidField] = maxOid;
              fs_hydro_soil.features.push(newFeature);
            }
          }
        }
        result = fs_hydro_soil;
      }
    } catch (e) {
      if ((e as any).message.toLowerCase().includes("unable to complete operation") || ((e as any).code === 400) || (e as any).message.toLowerCase().includes("failed to fetch") || (e as any).message.toLowerCase().includes("mapserver not started")) {
        throw (e);
      }
      result = undefined;
    }
    return result;
  }

  async fetchSalFeatureSets(watershedFeatureSet: FeatureSet): Promise<IFetchSalFeatureSetsResponse | null> {
    const query = new Query();
    query.spatialRelationship = 'intersects';
    query.returnGeometry = true;
    query.where = '1=1';
    query.outFields = ['*'];
    query.geometry = watershedFeatureSet.features[0].geometry;
    query.outSpatialReference = new SpatialReference({ wkid: 3857 });

    // Check the LULC cover the watershed in its entirety
    const watershed = watershedFeatureSet.features[0].geometry;
    const fs_lulc = await esriQuery.executeQueryJSON(this.__lulcMapServerUrl, query);
    const fs_lulc_geometries = fs_lulc.features.map((feature) => {
      return feature.geometry;
    });
    const fs_lulc_union = esriGeometryEngine.union(fs_lulc_geometries);

    const innerGeometry = watershed;
    let outerGeometry = fs_lulc_union;

    if (innerGeometry.spatialReference.wkid !== outerGeometry.spatialReference.wkid) {
      await esriProjection.load();
      outerGeometry = esriProjection.project(outerGeometry, innerGeometry.spatialReference) as Geometry;
    }

    const contains = esriGeometryEngine.contains(outerGeometry, innerGeometry);

    // Check the Soil cover the watershed in its entirety
    query.where = "MUKEY>0";

    const fs_hydro_soil = await esriQuery.executeQueryJSON(this.__soilMapServerUrl, query);

    const fs_soil_geometries = fs_hydro_soil.features.map((feature) => {
      return feature.geometry;
    });

    const unionResult = esriGeometryEngine.union(fs_soil_geometries);
    await esriProjection.load();
    const unionResultProject = esriProjection.project(unionResult, watershed.spatialReference) as Geometry;

    const result = esriGeometryEngine.difference(watershed, unionResultProject);

    if (result === null) {
      if (!contains) {
        await MessageCenter.addWarningFromCode('Hydro_78', [
          {
            callback: () => {
              MessageCenter.clearMessages();
            },
            text: 'Cancel',
          },
        ]);
      }
      return { fs_lulc, fs_soil: fs_hydro_soil };
    }

    return null;
  }
}

export interface IInvalidateGpRequest {
  sys_message_prefix: string;
  in_server_folder: string;
  in_session_id: string;
  in_step: number;
  in_full_delete: boolean;
  result_success?: boolean;
}
export interface IInvalidateGpResponse {
  in_session_id: string;
  in_step: number;
  sys_message_prefix: string;
  in_full_delete: boolean;
  result_success: boolean;
}

export interface IFeatureResult {
  result_feature_set?: FeatureSet;
  result_feature_layer?: FeatureLayer;
}

export interface IDelineateWatershedRequestParams {
  in_dem?: RasterData | File;
  in_dem_elevation_units?: string;
  in_view_dem?: boolean;
  in_aoi?: FeatureSet;
  in_generate_contours?: boolean;
  in_contour_interval?: number;
  in_generate_hillshade?: boolean;
  in_generate_depthgrid?: boolean;
  in_culverts?: FeatureSet;
  in_generate_streamlines?: boolean;
  in_stream_threshold?: number;
  in_outlets?: FeatureSet;
  in_sessionId: string;
  in_projection: number;
  in_upd_watershed?: FeatureSet;
}

export interface IConvertToGeotiffResponse {
  result_geotiff_file?: string,
  result_modify_flag?: boolean
}

export interface IDemUploadResponse {
  result_dem_layer?: MapImageLayer;
  result_dem_boundary?: FeatureSet;
  result_dem_boundary_layer?: FeatureLayer;
}

export interface IAoiDemResponse {
  result_clipdem_layer?: MapImageLayer;
  result_aoi?: FeatureSet;
  result_aoi_layer?: FeatureLayer;
}

export interface IStreamlineResponse {
  result_excess_stream_count?: boolean;
  result_streamlines?: FeatureSet;
  result_streamlines_layer?: FeatureLayer;
  result_culverts?: FeatureSet;
  result_culverts_layer?: FeatureLayer;
}

export interface IWatershedResponse {
  result_watershed?: FeatureSet;
  result_watershed_layer?: FeatureLayer;
  result_flowpath?: FeatureSet;
  result_flowpath_layer?: FeatureLayer;
  result_flowpath_orig?: FeatureSet;
  result_flowpath_orig_layer?: FeatureLayer;
  result_outlets?: FeatureSet;
  result_outlets_layer?: FeatureLayer;
}

export interface ICalculateRcnResponse {
  result_rcn_watershed?: FeatureSet;
  result_rcn_watershed_layer?: FeatureLayer;
  result_rcn_subareas?: FeatureSet;
  result_rcn_subareas_layer?: FeatureLayer;
}

export interface ILoadSessionResponse {
  result_dem_boundary?: FeatureSet;
  result_dem_boundary_layer?: FeatureLayer;
  result_aoi?: FeatureSet;
  result_aoi_layer?: FeatureLayer;
  result_streamlines?: FeatureSet;
  result_streamlines_layer?: FeatureLayer;
  result_culverts?: FeatureSet;
  result_culverts_layer?: FeatureLayer;
  result_outlets?: FeatureSet;
  result_outlets_layer?: FeatureLayer;
  result_watershed?: FeatureSet;
  result_watershed_layer?: FeatureLayer;
  result_flowpath?: FeatureSet;
  result_flowpath_layer?: FeatureLayer;
  result_flowpath_orig?: FeatureSet;
  result_flowpath_orig_layer?: FeatureLayer;
  result_soils?: FeatureSet;
  result_soils_layer?: FeatureLayer;
  result_landuse?: FeatureSet;
  result_landuse_layer?: FeatureLayer;
  result_rcn_watershed?: FeatureSet;
  result_rcn_watershed_layer?: FeatureLayer;
  result_rcn_subareas?: FeatureSet;
  result_rcn_subareas_layer?: FeatureLayer;
  // result_rcn_in_soils?: FeatureSet;
  // result_rcn_in_soils_layer?: FeatureLayer;
  // result_rcn_in_landuse?: FeatureSet;
  // result_rcn_in_landuse_layer?: FeatureLayer;
  //=================
  result_dem_layer?: MapImageLayer;
  result_clipdem_layer?: MapImageLayer;
  result_hillshade_layer?: MapImageLayer;
  result_depthgrid_layer?: MapImageLayer;
  result_contours_layer?: MapImageLayer;
}

export interface IDelineateWatershedResponse {
  out_result_manifest: string[];

  result_dem?: RasterData;
  result_clipdem?: RasterData;
  result_hillshade?: RasterData;
  result_depthgrid?: RasterData;
  result_dem_boundary?: FeatureSet;
  result_aoi?: FeatureSet;
  result_culverts?: FeatureSet;
  result_outlets?: FeatureSet;
  result_contours?: FeatureSet; //note: expensive, consider removing
  result_streamlines?: FeatureSet;
  result_watershed?: FeatureSet;
  result_upd_watershed?: FeatureSet;
  result_flowpath?: FeatureSet;

  result_dem_layer?: MapImageLayer;
  result_clipdem_layer?: MapImageLayer;
  result_hillshade_layer?: MapImageLayer;
  result_depthgrid_layer?: MapImageLayer;
  result_dem_boundary_layer?: FeatureLayer;
  result_aoi_layer?: FeatureLayer;
  result_culverts_layer?: FeatureLayer;
  result_outlets_layer?: FeatureLayer;
  result_contours_layer?: FeatureLayer; //note: expensive, consider removing, or replacing with MapImageService
  result_streamlines_layer?: FeatureLayer;
  result_watershed_layer?: FeatureLayer;
  result_flowpath_layer?: FeatureLayer;
}

export interface IPrepareSalResponse {
  out_result_manifest: string[];

  output_SAL_WATERSHED?: FeatureSet;
  result_soils?: FeatureSet;
  output_SAL_LANDUSE?: FeatureSet;

  output_SAL_WATERSHED_layer?: FeatureLayer;
  result_soils_layer?: FeatureLayer;
  // output_SAL_LANDUSE_layer?: FeatureLayer;
}

export interface ILoadSessionEfh2Response {
  out_result_manifest: string[];

  result_dem?: RasterData;
  result_clipdem?: RasterData;
  result_hillshade?: RasterData;
  result_depthgrid?: RasterData;
  result_dem_boundary?: FeatureSet;
  result_aoi?: FeatureSet;
  result_culverts?: FeatureSet;
  result_outlets?: FeatureSet;
  result_contours?: FeatureSet; //note: expensive, consider removing
  result_streamlines?: FeatureSet;
  result_watershed?: FeatureSet;
  result_flowpath?: FeatureSet;

  output_SAL_WATERSHED?: FeatureSet;
  result_soils?: FeatureSet;
  output_SAL_LANDUSE?: FeatureSet;
  output_RCN_WATERSHED?: FeatureSet;
  output_RCN_SUBAREAS?: FeatureSet;

  result_dem_layer?: MapImageLayer;
  result_clipdem_layer?: MapImageLayer;
  result_hillshade_layer?: MapImageLayer;
  result_depthgrid_layer?: MapImageLayer;
  result_dem_boundary_layer?: FeatureLayer;
  result_aoi_layer?: FeatureLayer;
  result_culverts_layer?: FeatureLayer;
  result_outlets_layer?: FeatureLayer;
  result_contours_layer?: MapImageLayer; //note: expensive, consider removing
  result_streamlines_layer?: FeatureLayer;
  result_watershed_layer?: FeatureLayer;
  result_flowpath_layer?: FeatureLayer;

  output_SAL_WATERSHED_layer?: FeatureLayer;
  result_soils_layer?: FeatureLayer;
  output_RCN_WATERSHED_layer?: FeatureLayer;
  output_RCN_SUBAREAS_layer?: FeatureLayer;
}

export interface IFetchSalFeatureSetsResponse {
  fs_lulc: __esri.FeatureSet;
  fs_soil: __esri.FeatureSet;
}

export interface IDelineateWatershedLayersConfig {
  result_dem_layer?: IMapImageLayerConfig;
  result_clipdem_layer?: IMapImageLayerConfig;
  result_hillshade_layer?: IMapImageLayerConfig;
  result_depthgrid_layer?: IMapImageLayerConfig;
  result_contours_layer?: IMapImageLayerConfig;
  result_dem_boundary_layer?: IFeatureLayerConfig;
  result_aoi_layer?: IFeatureLayerConfig;
  result_culverts_layer?: IFeatureLayerConfig;
  result_outlets_layer?: IFeatureLayerConfig;
  result_streamlines_layer?: IFeatureLayerConfig;
  result_watershed_layer?: IFeatureLayerConfig;
  result_flowpath_layer?: IFeatureLayerConfig;
  result_flowpath_orig_layer?: IFeatureLayerConfig;
  output_SAL_WATERSHED_layer?: IFeatureLayerConfig;
  result_soils_layer?: IFeatureLayerConfig;
  result_rcn_in_soils_layer?: IFeatureLayerConfig;
  output_RCN_WATERSHED_layer?: IFeatureLayerConfig;
  output_RCN_SUBAREAS_layer?: IFeatureLayerConfig;
}

export interface IMapImageLayerConfig {
  title?: string;
  visible?: boolean;
  listMode?: 'hide-children' | 'show' | 'hide';
  opacity?: number;
}

export interface IFeatureLayerConfig {
  title?: string;
  visible?: boolean;
  listMode?: 'hide-children' | 'show' | 'hide';
  renderer?: __esri.Renderer;
}

export interface IGraphicsLayerConfig {
  title?: string;
  visible?: boolean;
  listMode?: 'hide-children' | 'show' | 'hide';
  symbol?: __esri.Symbol;
}

const __resultLayersConfig: IDelineateWatershedLayersConfig = {
  result_dem_layer: { title: 'DEM (original)', visible: true, listMode: 'hide-children', opacity: 0.5 },
  result_clipdem_layer: { title: 'DEM (clipped)', visible: true, listMode: 'hide-children', opacity: 0.5 },
  result_depthgrid_layer: { title: 'DepthGrid', visible: true, listMode: 'hide-children', opacity: 0.5 },
  result_hillshade_layer: { title: 'Hillshade', visible: true, listMode: 'hide-children', opacity: 0.5 },
  result_contours_layer: { title: 'Contours', visible: true, listMode: 'hide-children', opacity: 1.0 },

  result_dem_boundary_layer: {
    title: 'DEM (outline)',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'solid',
          color: 'black',
          width: '2px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },
  result_aoi_layer: {
    title: 'AOI',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'short-dash',
          color: 'red',
          width: '2px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },

  result_culverts_layer: {
    title: 'Hydraulic Connections',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'short-dash',
          color: 'blue',
          width: '2px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },
  result_outlets_layer: {
    title: 'Outlet',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'short-dash',
          color: 'green',
          width: '2px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },

  result_streamlines_layer: {
    title: 'Streams',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'solid',
          color: 'blue',
          width: '1px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },
  result_watershed_layer: {
    title: 'Watershed (computed)',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'solid',
          color: 'white',
          width: '2px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },
  result_flowpath_layer: {
    title: 'Flow Path',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'solid',
          color: 'blue',
          width: '2px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },
  result_flowpath_orig_layer: {
    title: 'Original Flow Path',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'short-dot',
          color: 'blue',
          width: '6px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },

  output_SAL_WATERSHED_layer: {
    title: 'Watershed (edit)',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'short-dash',
          color: 'white',
          width: '4px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },
  result_soils_layer: {
    title: 'Soil',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'dot',
          color: 'black',
          width: '2px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },
  result_rcn_in_soils_layer: {
    title: 'Soil',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'dot',
          color: 'black',
          width: '2px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },
  output_RCN_WATERSHED_layer: {
    title: 'Watershed (final)',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'solid',
          color: 'yellow',
          width: '4px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },
  output_RCN_SUBAREAS_layer: {
    title: 'RCN Components',
    visible: true,
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-fill',
        style: 'none',
        color: [0, 0, 0, 0],
        outline: {
          type: 'simple-line',
          style: 'short-dash',
          color: 'yellow',
          width: '2px',
        } as unknown, //SimpleLineSymbol
      } as unknown, //SimpleFillSymbol
    } as any, //SimpleRenderer
  },
};
